import { useState, Fragment } from 'react'
import { Play, ChevronLeft, ChevronRight, ChevronDown, Check, X, ExternalLink } from 'lucide-react'
import type { CallRecord, PaginationInfo } from '../types'
import { CLASSIFICATION_STYLES, IRRELEVANT_REASON_LABELS } from '../types'
import { cn } from '@/lib/utils'

interface CallLogTableProps {
  calls: CallRecord[]
  pagination: PaginationInfo
  onPageChange: (page: number) => void
  onPlayRecording?: (resourceId: string) => void
}

export function CallLogTable({ calls, pagination, onPageChange, onPlayRecording }: CallLogTableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const totalPages = pagination.total_pages || Math.ceil(pagination.total / pagination.per_page)

  const handleRowClick = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id)
  }

  const getScoreColor = (score: number | null) => {
    if (score === null || score === undefined) return ''
    if (score >= 80) return 'bg-emerald-100 text-emerald-700'
    if (score >= 60) return 'bg-yellow-100 text-yellow-700'
    if (score >= 40) return 'bg-orange-100 text-orange-700'
    return 'bg-red-100 text-red-600'
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Caller
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                New/Return
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Paid (GCLID)
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tags
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI Summary
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {calls.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  No calls found for this period
                </td>
              </tr>
            ) : (
              calls.map((call) => (
                <Fragment key={call.id}>
                <tr
                  className={cn(
                    'hover:bg-gray-50 cursor-pointer transition-colors',
                    expandedRow === call.id && 'bg-blue-50'
                  )}
                  onClick={() => handleRowClick(call.id)}
                >
                  {/* Date & Time */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{call.date || '-'}</div>
                    <div className="text-xs text-gray-500">{call.time || '-'}</div>
                  </td>

                  {/* Duration */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {call.duration_formatted || '0:00'}
                  </td>

                  {/* Caller */}
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                      {call.customer_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">{call.customer_phone || '-'}</div>
                  </td>

                  {/* New/Return */}
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex px-2 py-0.5 text-xs font-medium rounded',
                        call.customer_type === 'new'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {call.customer_type === 'new' ? 'NEW' : 'RETURN'}
                    </span>
                  </td>

                  {/* Paid (GCLID) */}
                  <td className="px-4 py-3 text-center">
                    {call.has_gclid ? (
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mx-auto" />
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-center">
                    {call.classification ? (
                      <span
                        className={cn(
                          'inline-flex px-2 py-0.5 text-xs font-medium rounded',
                          CLASSIFICATION_STYLES[call.classification]?.bg || 'bg-gray-100',
                          CLASSIFICATION_STYLES[call.classification]?.text || 'text-gray-600'
                        )}
                      >
                        {CLASSIFICATION_STYLES[call.classification]?.label || call.classification}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>

                  {/* Tags - only show for NEW customers */}
                  <td className="px-4 py-3">
                    {call.customer_type === 'new' && (
                      <div className="flex flex-wrap gap-1">
                        {call.appliance_type && call.appliance_type !== 'unknown' && (
                          <span className="inline-flex px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {call.appliance_type}
                          </span>
                        )}
                        {call.appliance_brand && call.appliance_brand !== 'unknown' && (
                          <span className="inline-flex px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {call.appliance_brand}
                          </span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Score - only show for NEW customers */}
                  <td className="px-4 py-3 text-center">
                    {call.customer_type === 'new' && call.score !== null && call.score !== undefined ? (
                      <span className={cn('inline-flex px-2 py-0.5 text-sm font-bold rounded', getScoreColor(call.score))}>
                        {call.score}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>

                  {/* AI Summary */}
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-1">
                      <div className="text-sm text-gray-600 truncate max-w-[200px]">
                        {call.classification_reason || '-'}
                      </div>
                      {call.classification_reason && (
                        <ChevronDown className={cn(
                          "w-4 h-4 text-gray-400 shrink-0 transition-transform",
                          expandedRow === call.id && "rotate-180"
                        )} />
                      )}
                    </div>
                    {call.irrelevant_reason && (
                      <div className="text-xs text-red-500 mt-0.5">
                        {IRRELEVANT_REASON_LABELS[call.irrelevant_reason] || call.irrelevant_reason}
                      </div>
                    )}
                    {call.booked === true && (
                      <div className="text-xs text-emerald-600 font-medium mt-0.5">
                        Booked
                      </div>
                    )}
                    {call.customer_type !== 'new' && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        Return Customer
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-center">
                    {call.recording_url && onPlayRecording && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          onPlayRecording(call.resource_id)
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Play recording"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
                {/* Expanded AI Summary Row */}
                {expandedRow === call.id && (
                  <tr className="bg-blue-50 border-b border-blue-100">
                    <td colSpan={10} className="px-4 py-4">
                      <div className="text-sm text-gray-700">
                        <strong className="text-gray-900">AI Summary:</strong>
                        <p className="mt-1 whitespace-pre-wrap">{call.classification_reason || 'No summary available'}</p>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
            {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} calls
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
